import { Project, ProjectDocument } from '../../../../types'
import { getProjectDocumentName } from '../../../../utils/projectDocument'

const RESERVED_DOCUMENT_NAMES = new Set([
    'con',
    'prn',
    'aux',
    'nul',
    'com1',
    'com2',
    'com3',
    'com4',
    'com5',
    'com6',
    'com7',
    'com8',
    'com9',
    'lpt1',
    'lpt2',
    'lpt3',
    'lpt4',
    'lpt5',
    'lpt6',
    'lpt7',
    'lpt8',
    'lpt9',
])

export function getDocumentDate(document: ProjectDocument) {
    return document.created_at || document.uploaded_at || document.updated_at || ''
}

export function isArchivedDocument(document: ProjectDocument) {
    return Boolean(document.is_archived || document.archived_at || document.status === 'archived')
}

export function isDeletedDocument(document: ProjectDocument) {
    if (isArchivedDocument(document)) {
        return false
    }

    return Boolean(document.is_deleted || document.deleted_at || document.status === 'deleted')
}

export function getDocumentOwner(document: ProjectDocument) {
    return document.owner || document.created_by || document.author || null
}

export function isOwnedByCurrentUser(
    document: ProjectDocument,
    currentUserIds: Array<string | number> = [],
    currentUserEmails: string[] = [],
) {
    if (!currentUserIds.length && !currentUserEmails.length) {
        return true
    }

    const owner = getDocumentOwner(document)
    const documentUserIds = [
        document.owner_id,
        document.user_id,
        document.created_by_id,
        owner?.id,
        owner?.user_id,
        document.created_by?.id,
        document.created_by?.user_id,
        document.author?.id,
        document.author?.user_id,
    ]

    if (currentUserIds.some((currentUserId) => documentUserIds.some((value) => value != null && String(value) === String(currentUserId)))) {
        return true
    }

    const documentEmails = [
        owner?.email,
        document.created_by?.email,
        document.author?.email,
    ]

    if (currentUserEmails.some((currentUserEmail) => documentEmails.some((value) => value && value === currentUserEmail))) {
        return true
    }

    return false
}

export function getDocumentOwnerName(document: ProjectDocument) {
    const owner = getDocumentOwner(document)
    const profileName = [owner?.profile?.first_name, owner?.profile?.last_name].filter(Boolean).join(' ')
    const directName = [owner?.first_name, owner?.last_name].filter(Boolean).join(' ')

    return profileName
        || directName
        || owner?.full_name
        || owner?.name
        || owner?.email
        || 'Не указан'
}

export function getDocumentOwnerInitials(document: ProjectDocument) {
    const ownerName = getDocumentOwnerName(document)
    const parts = ownerName.split(' ').filter(Boolean)

    if (!parts.length) {
        return 'N'
    }

    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('')
}

export function formatDocumentDate(value: string) {
    if (!value) return '-'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return value
    }

    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}

export function extractProjects(data: any): Project[] {
    if (Array.isArray(data)) {
        return data
    }

    if (Array.isArray(data?.projects)) {
        return data.projects
    }

    if (Array.isArray(data?.items)) {
        return data.items
    }

    return []
}

export function getMovePopoverPosition(rect: DOMRect | null) {
    const popoverWidth = 310
    const left = rect ? Math.max(16, rect.left - popoverWidth - 16) : 48
    const top = rect
        ? Math.min(window.innerHeight - 240, Math.max(16, rect.top - 72))
        : 120

    return { left, top }
}

export function splitDocumentNameParts(document: ProjectDocument) {
    const fullName = getProjectDocumentName(document).trim()
    const lastDotIndex = fullName.lastIndexOf('.')

    if (lastDotIndex <= 0 || lastDotIndex === fullName.length - 1) {
        return {
            baseName: fullName,
            extension: '',
        }
    }

    return {
        baseName: fullName.slice(0, lastDotIndex),
        extension: fullName.slice(lastDotIndex),
    }
}

export function validateDocumentRenameName(value: string) {
    const trimmedValue = value.trim()

    if (!trimmedValue) {
        return 'Название документа не может быть пустым.'
    }

    if (trimmedValue.length > 200) {
        return 'Название документа не должно превышать 200 символов.'
    }

    if (/[*?"<>|]/.test(trimmedValue)) {
        return 'Название документа содержит запрещенные символы: * ? " < > |'
    }

    if (trimmedValue.includes('/')) {
        return 'Название документа не должно содержать символ /'
    }

    if (trimmedValue.includes('\\')) {
        return 'Название документа не должно содержать символ \\'
    }

    if (trimmedValue.includes('.')) {
        return 'Название документа не должно содержать точки.'
    }

    if (RESERVED_DOCUMENT_NAMES.has(trimmedValue.toLowerCase())) {
        return 'Это зарезервированное имя, используйте другое название.'
    }

    return null
}

export function buildDocumentRenameValue(document: ProjectDocument, nextBaseName: string) {
    const validationError = validateDocumentRenameName(nextBaseName)

    if (validationError) {
        return {
            error: validationError,
            value: null,
        }
    }

    const trimmedBaseName = nextBaseName.trim()

    return {
        error: null,
        value: trimmedBaseName,
    }
}
